import clsx from "clsx";
import { useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";

import { Button } from "../../../components/atoms/Buttons/Button";
import { PanelHeader } from "../../../components/features/SquadBuilder/RightPanel/PanelHeader/PanelHeader";
import { useScrollLock } from "../../../hooks/useScrollLock";
import {
  PlayerRole,
  playerRoleLabelSelector,
} from "../../../redux/players/playersSlice";

type Props = {
  playerRoles: PlayerRole[];
  selectedPlayerRole: PlayerRole;
  handleSelectPlayerRole: (pr: PlayerRole) => void;
  handleOnClose: () => void;
  shouldStickToTop: boolean;
};

export const roleToRolesSection = {
  1: "GK", // Goalkeeper
  2: "GK", // Sweeper Keeper
  3: "RB", // Fullback
  4: "RB", // Falseback
  5: "RB", // Wingback
  6: "RB", // Attacking Wingback
  7: "LB", // Fullback
  8: "LB", // Falseback
  9: "LB", // Wingback
  10: "LB", // Attacking Wingback
  11: "CB", // Defender
  12: "CB", // Stopper
  13: "CB", // Ball-Playing Defender
  14: "CDM", // Holding
  15: "CDM", // Centre-Half
  16: "CDM", // Deep-Lying Playmaker
  17: "CDM", // Wide Half
  18: "CM", // Box-to-Box
  19: "CM", // Holding
  20: "CM", // Deep-Lying Playmaker
  21: "CM", // Playmaker
  22: "CM", // Half-Winger
  23: "RM", // Winger
  24: "RM", // Wide Midfielder
  25: "RM", // Wide Playmaker
  26: "RM", // Inside Forward
  27: "LM", // Winger
  28: "LM", // Wide Midfielder
  29: "LM", // Wide Playmaker
  30: "LM", // Inside Forward
  31: "CAM", // Playmaker
  32: "CAM", // Shadow Striker
  33: "CAM", // Half-Winger
  34: "CAM", // Classic 10
  35: "RW", // Winger
  36: "RW", // Inside Forward
  37: "RW", // Wide Playmaker
  38: "LW", // Winger
  39: "LW", // Inside Forward
  40: "LW", // Wide Playmaker
  41: "ST", // Advanced Forward
  42: "ST", // Poacher
  43: "ST", // False 9
  44: "ST", // Target Forward
};

const PlayeRoleButton = ({
  children,
  onClick,
  active,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
}) => (
  <button
    className={clsx(
      "w-full rounded-md h-[28px] text-xs",
      active ? "bg-primary-blue text-white" : "bg-white/10 text-gray-300 ",
    )}
    onClick={onClick}
  >
    {children}
  </button>
);

export const SelectPlayerRole = ({
  playerRoles,
  selectedPlayerRole,
  handleSelectPlayerRole,
  handleOnClose,
  shouldStickToTop,
}: Props) => {
  const overlayRef = useRef(null);
  const playerRoleLabel = useSelector(
    playerRoleLabelSelector({
      playerRoleId: selectedPlayerRole?.id,
    }),
  );
  const [localSelectPlayerRole, setLocalSelectPlayerRole] =
    useState<PlayerRole>(selectedPlayerRole);

  // Lock scroll on mobile devices
  useScrollLock(isMobile);

  let groupedRoles = playerRoles.reduce(
    (acc, role) => {
      const section = roleToRolesSection[role.id];
      if (section) {
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(role);
      }
      return acc;
    },
    {} as Record<string, PlayerRole[]>,
  );

  const handleLocalSelectPlayerRole = (role: PlayerRole) => {
    setLocalSelectPlayerRole(role);
    handleSelectPlayerRole(role);
  };

  return (
    <div
      className={clsx(
        "w-full desktop:w-[352px] right-[max(0px,calc((100vw-2350px)/2))] bg-gray-900 desktop:bg-side-panel desktop:shadow-right-panel overflow-y-auto z-30 fixed top-0 bottom-0",
        shouldStickToTop
          ? "desktop:fixed desktop:top-0 desktop:bottom-0"
          : "desktop:absolute desktop:top-10 desktop:-bottom-12.5",
      )}
      ref={overlayRef}
    >
      <div className="flex flex-col gap-y-6 fixed mobile:relative bg-gray-900 mobile:bg-transparent">
        <PanelHeader title={"Select Player Role"} onClose={handleOnClose} />
        <div className="flex flex-col mb-5 text-xs h-[90px] desktop:h-[100px] px-8">
          <p className="pb-1 text-white">{playerRoleLabel || "Any Role"}</p>
          <p className="text-gray-300">
            {selectedPlayerRole?.description ||
              'By selecting "Any" as a role, you will see all the players with their respective best roles '}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-y-3 mt-48 mobile:mt-0 px-8 pb-20 desktop:pb-[120px]">
        <div>
          <h3 className="pb-2 ml-2 text-xs text-white">General</h3>
          <ul className="grid grid-cols-2 gap-1">
            <PlayeRoleButton
              key={"general"}
              onClick={() => handleLocalSelectPlayerRole(undefined)}
              active={!localSelectPlayerRole}
            >
              Any
            </PlayeRoleButton>
          </ul>
        </div>
        {Object.entries(groupedRoles).map(([section, roles]) => (
          <div key={section}>
            <h3 className="pb-2 ml-2 text-xs text-white">{section}</h3>
            <ul className="grid grid-cols-2 gap-1">
              {roles.map((role) => (
                <PlayeRoleButton
                  key={role.id}
                  active={localSelectPlayerRole?.id === role.id}
                  onClick={() => handleLocalSelectPlayerRole(role)}
                >
                  {role.name}
                </PlayeRoleButton>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 w-full px-8 gap-x-3 desktop:w-[352px] fixed bottom-4">
        <Button
          variant="tertiary"
          size="large"
          onClick={() => {
            handleLocalSelectPlayerRole(undefined);
            handleOnClose();
          }}
        >
          Cancel
        </Button>
        <Button size="large" onClick={handleOnClose}>
          OK
        </Button>
      </div>
    </div>
  );
};
